import { Fragment, useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { GovItem } from "../../../../utils/icGovernance";
import { CARD_BORDER, PRIMARY, SignOffChip, StatusChip, dateOnly, itemUrl } from "../governanceStatus";
import { INK, MONO, MUTED } from "./cells";

/** Pieces of RegisterTable: the amber "waiting for you" notice and the phone card. */

export interface WaitingNotice<T extends GovItem> {
  /** Why this row waits for the admin's decision, or null. */
  reason: (row: T) => string | null;
  /** The sentence before the IDs, e.g. (1, …) → "1 task waits for your sign-off". */
  describe: (count: number, reason: string) => string;
}

const NOTICE_ID_LIMIT = 8;

// ---------------------------------------------------------------------------
// Waiting notice
// ---------------------------------------------------------------------------

type NoticeProps<T extends GovItem> = { rows: T[]; waiting: WaitingNotice<T>; partial: boolean };

export const WaitingNoticeBox = <T extends GovItem>({ rows, waiting, partial }: NoticeProps<T>) => {
  const groups = useMemo(() => {
    const map = new Map<string, T[]>();
    rows.forEach((r) => {
      const reason = waiting.reason(r);
      if (!reason) return;
      map.set(reason, [...(map.get(reason) ?? []), r]);
    });
    return Array.from(map.entries());
  }, [rows, waiting]);

  if (groups.length === 0) return null;
  return (
    <Box
      role="status"
      sx={{
        mb: 2,
        px: 1.75,
        py: 1.25,
        borderRadius: "8px",
        backgroundColor: "#fef3c7",
        border: "1px solid #f59e0b",
        color: "#92400e",
        fontSize: 14,
      }}
    >
      {groups.map(([reason, list]) => (
        <Box key={reason} sx={{ fontWeight: 600 }}>
          {waiting.describe(list.length, reason)}:{" "}
          {list.slice(0, NOTICE_ID_LIMIT).map((r, i) => (
            <Fragment key={r.id}>
              {i > 0 ? ", " : null}
              <Link to={itemUrl(r)} style={{ fontFamily: MONO, color: "#92400e" }}>
                {r.displayId}
              </Link>
            </Fragment>
          ))}
          {list.length > NOTICE_ID_LIMIT ? ` and ${list.length - NOTICE_ID_LIMIT} more` : null}
        </Box>
      ))}
      {partial ? (
        <Box sx={{ fontWeight: 400, mt: 0.25 }}>Counted from the rows loaded so far — load more to see the rest.</Box>
      ) : null}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Phone cards
// ---------------------------------------------------------------------------

export const RegisterCard = <T extends GovItem>({ row }: { row: T }) => (
  <Box
    component={Link}
    to={itemUrl(row)}
    sx={{
      display: "block",
      padding: "12px 16px",
      borderBottom: CARD_BORDER,
      color: "inherit",
      textDecoration: "none",
      "&:active": { backgroundColor: "#f5f5f5" },
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Typography variant="body2" sx={{ fontFamily: MONO, fontWeight: 700, color: PRIMARY }}>
        {row.displayId}
      </Typography>
      <StatusChip kind={row.kind} status={row.status} />
    </Stack>
    <Typography sx={{ color: INK, fontWeight: 600, mt: 0.5 }}>{row.title || "(untitled)"}</Typography>
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ mt: 0.5, flexWrap: "wrap" }}>
      <Typography variant="body2" sx={{ color: MUTED }}>
        Due {dateOnly(row.dueDate)}
      </Typography>
      {row.signOff && row.signOff.status !== "NONE" ? (
        <>
          <Typography variant="body2" sx={{ color: MUTED }}>
            · Sign-off
          </Typography>
          <SignOffChip status={row.signOff.status} />
        </>
      ) : null}
    </Stack>
  </Box>
);
